import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  businessDayFetchRange,
  computeActivityPresence,
  computeDayWorkedHours,
  frDayLabel,
  todayIsoParis,
  type ActivityOverride,
  type ActivityPresence,
  type ActivityQualification,
  type BoardActivity,
  type BoardDay,
  type DayPresence,
  type Employee,
} from '@lemontaigu/staff';
import { useBoard, useRoster, useTimesheet } from '@lemontaigu/staff/react';
import { PresenceMatrix } from '@lemontaigu/reception/team/PresenceMatrix.js';
import { ChargeWeek, useChargeConfig } from '@lemontaigu/reception/team/ChargeBoard.js';
import { FrDateInput } from '@lemontaigu/reception/ui/FrDateInput.js';
import {
  fmtMonthLabel,
  monthDays,
  monthStart,
  shiftMonthStart,
  weekDays,
  weekStartKey,
} from '@lemontaigu/reception/team/logic.js';
import { shiftDay } from './logic.js';
import { SelfQuickEdit } from './SelfQuickEdit.js';
import { Presence } from './Presence.js';

// « Planning » (staff.*, 2026-07-19; weekly view added 2026-07-21): the
// individual's read-only view of the « Présence » matrix — who's
// declared/assigned matin/après-midi/soir or absent, team-grouped — in two
// horizons (Semaine / Mois, a Jour tab lives separately as « Présence »).
// Reuses @lemontaigu/reception's PresenceMatrix (the desk's own Semaine/Mois
// tabs render the same underlying data, heavier Gantt-style there) and its
// week/month-navigation helpers — same source of truth as reception/bos, but
// no desk-only demand entry or day/week editing surfaced here (that stays
// operator/desk-only, per the R2 site split). Each cell also shows worked
// hours (2026-07-19) — but ONLY for the signed-in employee's own column:
// staff_time_entries is queried scoped to their own id (RLS-safe, same
// self-only pattern as Pointages.tsx), so every other employee's column
// simply has no hours to show — never a team-wide payroll view.

// Column lists and row mappers moved into the C3 adapters at ladder step 6 —
// this file held the fifth copy of EMPLOYEE_COLUMNS and the fourth of
// ActivityPresenceRow.

type OverrideByDay = Map<string, ActivityOverride[]>;

// staff.* « Présence » is now ONE menu with three horizons (2026-07-21): the
// two former tabs (day « Présence » + « Planning » matrix) confused on a phone.
// Jour is the default — the single-day self-declare + activity-coverage gesture
// (apps/staff/src/Presence.tsx). Semaine / Mois show the read-only matrix.
type Mode = 'jour' | 'semaine' | 'mois';

const MODES: { mode: Mode; label: string }[] = [
  { mode: 'jour', label: 'Jour' },
  { mode: 'semaine', label: 'Semaine' },
  { mode: 'mois', label: 'Mois' },
];

export function Planning() {
  const [mode, setMode] = useState<Mode>('jour');
  return (
    <div>
      <div className="planning-modes">
        {MODES.map((m) => (
          <button
            key={m.mode}
            type="button"
            className={mode === m.mode ? 'is-active' : ''}
            onClick={() => setMode(m.mode)}
          >
            {m.label}
          </button>
        ))}
      </div>
      {mode === 'jour' ? <Presence /> : <MatrixPlanning mode={mode} />}
    </div>
  );
}

/** The read-only Présence matrix in the week / month horizon (bos@/staff@ share the source). */
function MatrixPlanning({ mode }: { mode: 'semaine' | 'mois' }) {
  const board = useBoard();
  const roster = useRoster();
  const timesheet = useTimesheet();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [activities, setActivities] = useState<BoardActivity[]>([]);
  // Untimed activities are excluded from the MATRIX (no Gantt bar) but are
  // perfectly declarable — the quick-edit offers the full active set, exactly
  // as the Jour tab does.
  const [allActivities, setAllActivities] = useState<BoardActivity[]>([]);
  const [qualifications, setQualifications] = useState<ActivityQualification[]>([]);
  const [editDay, setEditDay] = useState<string | null>(null);
  const [myEmployeeId, setMyEmployeeId] = useState<string | null>(null);
  const [month, setMonth] = useState(() => monthStart(todayIsoParis()));
  const [weekStart, setWeekStart] = useState(() => weekStartKey(todayIsoParis()));
  const [presencesByDay, setPresencesByDay] = useState<Map<string, ActivityPresence[]>>(new Map());
  const [overrides, setOverrides] = useState<OverrideByDay>(new Map());
  const [absencesByDay, setAbsencesByDay] = useState<Map<string, Set<string>>>(new Map());
  const [hoursByDay, setHoursByDay] = useState<Map<string, Map<string, number>>>(new Map());
  // The week's stored demand — « Charge de la semaine » only (operator,
  // 2026-09-05). Fetched for both horizons because the range read is one call
  // either way; only the Semaine horizon renders it (a 31-column month grid is
  // not a phone artefact, and the desk's Mois view already answers that
  // question with its own layout).
  const [boardDays, setBoardDays] = useState<Map<string, BoardDay>>(new Map());
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        // Current employees only (0031) — same roster as the day Présence tab.
        const [emps, acts, myId, quals] = await Promise.all([
          roster.current(),
          board.activities(),
          roster.myEmployeeId(),
          board.qualifications(),
        ]);
        setEmployees(emps);
        setAllActivities(acts);
        setQualifications(quals);
        // Untimed activities (« sans horaire ») have no place in the Gantt-based
        // week/month matrix — they live only on the reception day board for now.
        setActivities(acts.filter((a) => !a.untimed));
        setMyEmployeeId(myId);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [board, roster]);

  // [start, end) — end is the exclusive day AFTER the visible range, shared by
  // both horizons (a week is 7 days from weekStart; a month runs to the next
  // month's 1st).
  const load = useCallback(
    async (start: string, end: string, employeeId: string | null) => {
      setErr('');
      setLoaded(false);
      // clock_in is timestamptz, not date — bound it with real ISO instants, not
      // the bare 'YYYY-MM-DD' strings the date-typed board_* columns compare
      // against. businessDayFetchRange returns a UTC-anchored superset covering
      // the 04:00 business-day overhang; computeDayWorkedHours buckets exactly
      // and the matrix only reads the visible days' keys.
      const { fromIso: startIso, toIso: endIso } = businessDayFetchRange(start, end);
      // `end` is EXCLUSIVE here (the caller passes the day after the range) but
      // BoardStore's range is inclusive at both ends — step back one day to
      // fetch the exact window this view has always fetched.
      const endInclusive = shiftDay(end, -1);
      try {
        const [p, o, ab, t, bd] = await Promise.all([
          board.presence(start, endInclusive),
          board.overrides(start, endInclusive),
          board.absences(start, endInclusive),
          // Own hours only — never the whole team's (RLS also scopes this, but
          // stay explicit like Pointages.tsx rather than relying on it alone).
          employeeId ? timesheet.entriesBetween(startIso, endIso, employeeId) : Promise.resolve([]),
          board.days(start, endInclusive),
        ]);
        const presMap = new Map<string, ActivityPresence[]>();
        for (const parsed of p) presMap.set(parsed.date, [...(presMap.get(parsed.date) ?? []), parsed]);
        const overrideMap: OverrideByDay = new Map();
        for (const parsed of o) overrideMap.set(parsed.date, [...(overrideMap.get(parsed.date) ?? []), parsed]);
        const absMap = new Map<string, Set<string>>();
        for (const r of ab) {
          const set = absMap.get(r.date) ?? new Set<string>();
          set.add(r.employeeId);
          absMap.set(r.date, set);
        }
        setPresencesByDay(presMap);
        setOverrides(overrideMap);
        setAbsencesByDay(absMap);
        setHoursByDay(computeDayWorkedHours(t));
        setBoardDays(new Map(bd.map((d) => [d.date, d])));
        setLoaded(true);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    },
    [board, timesheet],
  );

  const rangeStart = mode === 'semaine' ? weekStart : month;
  const rangeEnd = mode === 'semaine' ? shiftDay(weekStart, 7) : shiftMonthStart(month, 1);

  useEffect(() => {
    void load(rangeStart, rangeEnd, myEmployeeId);
  }, [rangeStart, rangeEnd, myEmployeeId, load]);

  const days = useMemo(
    () => (mode === 'semaine' ? weekDays(weekStart) : monthDays(month)),
    [mode, weekStart, month],
  );
  const currentIds = useMemo(() => employees.map((e) => e.id), [employees]);
  const presenceByDay = useMemo(() => {
    const map = new Map<string, Map<string, DayPresence>>();
    for (const key of days) {
      map.set(
        key,
        computeActivityPresence({
          date: key,
          activities,
          overrides: overrides.get(key) ?? [],
          presences: presencesByDay.get(key) ?? [],
          absentEmployeeIds: absencesByDay.get(key) ?? [],
          currentEmployeeIds: currentIds,
        }),
      );
    }
    return map;
  }, [days, activities, overrides, presencesByDay, absencesByDay, currentIds]);

  const { registry, specs } = useChargeConfig();

  const today = todayIsoParis();
  const thisMonth = monthStart(today);
  const thisWeek = weekStartKey(today);

  return (
    <div className="planning-board">
      {mode === 'semaine' ? (
        <div className="staff-daybar">
          <button type="button" onClick={() => setWeekStart(shiftDay(weekStart, -7))} aria-label="Semaine précédente">
            ‹
          </button>
          <FrDateInput
            className="pt-datepicker"
            value={weekStart}
            onChange={(v) => v && setWeekStart(weekStartKey(v))}
          />
          <button type="button" onClick={() => setWeekStart(shiftDay(weekStart, 7))} aria-label="Semaine suivante">
            ›
          </button>
          <span className="staff-daylabel">
            Semaine du {frDayLabel(weekStart)} au {frDayLabel(shiftDay(weekStart, 6))}
          </span>
          {weekStart !== thisWeek && (
            <button type="button" className="staff-today" onClick={() => setWeekStart(thisWeek)}>
              Cette semaine
            </button>
          )}
        </div>
      ) : (
        <div className="staff-daybar">
          <button type="button" onClick={() => setMonth(shiftMonthStart(month, -1))} aria-label="Mois précédent">
            ‹
          </button>
          <FrDateInput className="pt-datepicker" value={month} onChange={(v) => v && setMonth(monthStart(v))} />
          <button type="button" onClick={() => setMonth(shiftMonthStart(month, 1))} aria-label="Mois suivant">
            ›
          </button>
          <span className="staff-daylabel">{fmtMonthLabel(month)}</span>
          {month !== thisMonth && (
            <button type="button" className="staff-today" onClick={() => setMonth(thisMonth)}>
              Ce mois-ci
            </button>
          )}
        </div>
      )}

      {err && <p className="auth-err">Erreur : {err}</p>}
      {!loaded && !err && <p className="staff-empty">Chargement…</p>}
      {/* « Charge de la semaine », read-only and compact — the desk's grid on a
          phone (operator, 2026-09-05). Above the presence matrix, same order as
          the Jour view: the week's load is the context you read the week's
          roster against. */}
      {loaded && mode === 'semaine' && (
        <ChargeWeek days={days} byDay={boardDays} registry={registry} specs={specs} compact />
      )}

      {loaded && (
        <PresenceMatrix
          employees={employees}
          days={days}
          presenceByDay={presenceByDay}
          today={today}
          hoursByDay={hoursByDay}
          title={mode === 'semaine' ? 'Présence de la semaine' : 'Présence du mois'}
          canEditCell={(id) => id === myEmployeeId}
          onCellClick={(_employeeId, date) => setEditDay(date)}
        />
      )}

      {editDay && myEmployeeId && (
        <SelfQuickEdit
          day={editDay}
          employeeId={myEmployeeId}
          activities={allActivities}
          qualifications={qualifications}
          onClose={() => setEditDay(null)}
          onChanged={() => void load(rangeStart, rangeEnd, myEmployeeId)}
        />
      )}
    </div>
  );
}
